import React, { useState, useRef, useEffect } from 'react';
import { 
  Play, 
  Upload, 
  Download, 
  Save, 
  Maximize2, 
  Minimize2, 
  Settings, 
  Copy, 
  RotateCcw,
  FileText,
  Zap,
  Shield,
  Smartphone,
  Globe,
  Code,
  CheckCircle,
  Eye,
  ChevronDown,
  AlertCircle,
  Info
} from 'lucide-react';

interface MinifierOptions {
  removeComments: boolean;
  removeWhitespace: boolean;
  removeConsole: boolean;
  removeDebugger: boolean;
  shortenVariables: boolean;
  optimizeConditions: boolean;
  removeEmptyFunctions: boolean;
  mangleNames: boolean;
}

interface CodeIssue {
  line: number;
  type: 'info' | 'warning' | 'error';
  message: string;
}

const JSMinifier: React.FC = () => {
  const [inputJS, setInputJS] = useState<string>(`// Advanced JavaScript Minifier Demo
function calculateTotal(items, taxRate) {
  // Calculate subtotal
  let subtotal = 0;
  
  for (let i = 0; i < items.length; i++) {
    subtotal += items[i].price * items[i].quantity;
  }
  
  // Apply tax
  const tax = subtotal * taxRate;
  const total = subtotal + tax;
  
  console.log('Subtotal:', subtotal);
  console.log('Tax:', tax);
  console.log('Total:', total);
  
  return total;
}

// Helper function to format currency
function formatCurrency(amount) {
  return '$' + amount.toFixed(2);
}

// Event handler
document.addEventListener('DOMContentLoaded', function() {
  const items = [
    { name: 'Product A', price: 29.99, quantity: 2 },
    { name: 'Product B', price: 15.50, quantity: 1 },
    { name: 'Product C', price: 8.75, quantity: 3 }
  ];
  
  const total = calculateTotal(items, 0.08);
  document.getElementById('total').textContent = formatCurrency(total);
});

// Empty function for demo
function unusedFunction() {
  
}

debugger; // This should be removed in production`);
  const [outputJS, setOutputJS] = useState<string>('');
  const [isFullscreen, setIsFullscreen] = useState<boolean>(false);
  const [viewMode, setViewMode] = useState<'code' | 'preview' | 'split'>('split');
  const [compressionRatio, setCompressionRatio] = useState<number>(0);
  const [originalSize, setOriginalSize] = useState<number>(0);
  const [minifiedSize, setMinifiedSize] = useState<number>(0);
  const [lineCount, setLineCount] = useState<number>(0);
  const [charCount, setCharCount] = useState<number>(0);
  const [wordCount, setWordCount] = useState<number>(0);
  const [codeIssues, setCodeIssues] = useState<CodeIssue[]>([]);
  
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [options, setOptions] = useState<MinifierOptions>({
    removeComments: true,
    removeWhitespace: true,
    removeConsole: true,
    removeDebugger: true,
    shortenVariables: false,
    optimizeConditions: true,
    removeEmptyFunctions: true,
    mangleNames: false
  });

  useEffect(() => {
    const lines = inputJS.split('\n').length;
    const chars = inputJS.length;
    const words = inputJS.trim() ? inputJS.trim().split(/\s+/).length : 0;
    
    setLineCount(lines);
    setCharCount(chars);
    setWordCount(words);
    
    // Analyze code issues
    const issues: CodeIssue[] = [];
    const jsLines = inputJS.split('\n');
    
    jsLines.forEach((line, index) => {
      if (line.includes('console.log')) {
        issues.push({
          line: index + 1,
          type: 'info',
          message: 'Console statement found - consider removing for production'
        });
      }
      if (line.includes('debugger')) {
        issues.push({
          line: index + 1,
          type: 'warning',
          message: 'Debugger statement should be removed in production'
        });
      }
      if (line.trim() === 'function' || (line.includes('function') && line.includes('{}')) || 
          (line.includes('function') && jsLines[index + 1]?.trim() === '}' && !jsLines[index + 1]?.includes('return'))) {
        issues.push({
          line: index + 1,
          type: 'info',
          message: 'Empty function detected'
        });
      }
      if (line.includes('var ')) {
        issues.push({
          line: index + 1,
          type: 'info',
          message: 'Consider using let or const instead of var'
        });
      }
    });
    
    setCodeIssues(issues);
  }, [inputJS]);

  const minifyJS = (js: string): string => {
    let minified = js;

    if (options.removeComments) {
      // Remove single-line comments
      minified = minified.replace(/\/\/.*$/gm, '');
      // Remove multi-line comments
      minified = minified.replace(/\/\*[\s\S]*?\*\//g, '');
    }

    if (options.removeConsole) {
      minified = minified.replace(/console\.(log|warn|error|info|debug)\([^)]*\);?/g, '');
    }

    if (options.removeDebugger) {
      minified = minified.replace(/debugger;?/g, '');
    }

    if (options.removeEmptyFunctions) {
      minified = minified.replace(/function\s+\w*\s*\(\s*\)\s*{\s*}/g, '');
      minified = minified.replace(/\(\s*\)\s*=>\s*{\s*}/g, '() => {}');
    }

    if (options.optimizeConditions) {
      minified = minified.replace(/if\s*\(\s*true\s*\)/g, 'if(true)');
      minified = minified.replace(/if\s*\(\s*false\s*\)/g, 'if(false)');
      minified = minified.replace(/===\s*true/g, '');
      minified = minified.replace(/!==\s*false/g, '');
    }

    if (options.removeWhitespace) {
      // Remove extra whitespace while preserving necessary spaces
      minified = minified.replace(/\n\s*\n/g, '\n'); // Remove empty lines
      minified = minified.replace(/\s+/g, ' '); // Replace multiple spaces with single space
      minified = minified.replace(/\s*([{}();,=+\-*/<>!&|])\s*/g, '$1'); // Remove spaces around operators
      minified = minified.replace(/;\s*}/g, '}'); // Remove semicolon before closing brace
      minified = minified.replace(/{\s*/g, '{'); // Remove space after opening brace
      minified = minified.replace(/\s*}/g, '}'); // Remove space before closing brace
    }

    if (options.shortenVariables) {
      // Basic variable shortening (simple implementation)
      const varMap = new Map();
      let counter = 0;
      const generateVarName = () => {
        const chars = 'abcdefghijklmnopqrstuvwxyz';
        let name = '';
        let num = counter++;
        do {
          name = chars[num % 26] + name;
          num = Math.floor(num / 26);
        } while (num > 0);
        return name;
      };

      // Find variable declarations and create mapping
      const varMatches = minified.match(/(?:let|const|var)\s+([a-zA-Z_$][a-zA-Z0-9_$]*)/g);
      if (varMatches) {
        varMatches.forEach(match => {
          const varName = match.split(/\s+/)[1];
          if (!varMap.has(varName) && varName.length > 2) {
            varMap.set(varName, generateVarName());
          }
        });

        // Replace variables
        varMap.forEach((shortName, longName) => {
          const regex = new RegExp(`\\b${longName}\\b`, 'g');
          minified = minified.replace(regex, shortName);
        });
      }
    }

    return minified.trim();
  };

  const handleMinify = (): void => {
    const minified = minifyJS(inputJS);
    setOutputJS(minified);
    
    const originalBytes = new Blob([inputJS]).size;
    const minifiedBytes = new Blob([minified]).size;
    const ratio = originalBytes > 0 ? ((originalBytes - minifiedBytes) / originalBytes * 100) : 0;
    
    setOriginalSize(originalBytes);
    setMinifiedSize(minifiedBytes);
    setCompressionRatio(Math.round(ratio * 100) / 100);
  };

  const handleUpload = (): void => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const file = event.target.files?.[0];
    if (file && (file.type === 'application/javascript' || file.type === 'text/javascript' || file.name.endsWith('.js'))) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const content = e.target?.result as string;
        setInputJS(content);
      };
      reader.readAsText(file);
    }
  };

  const handleDownload = (): void => {
    const blob = new Blob([outputJS], { type: 'application/javascript' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'minified.js';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleSave = (): void => {
    localStorage.setItem('advanced-js-minifier-input', inputJS);
    localStorage.setItem('advanced-js-minifier-output', outputJS);
  };

  const handleCopy = async (): Promise<void> => {
    try {
      await navigator.clipboard.writeText(outputJS);
    } catch (err) {
      console.error('Failed to copy text: ', err);
    }
  };

  const handleReset = (): void => {
    setInputJS('');
    setOutputJS('');
    setCompressionRatio(0);
    setOriginalSize(0);
    setMinifiedSize(0);
  };

  const formatBytes = (bytes: number): string => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <div className={`min-h-screen bg-gray-900 text-white ${isFullscreen ? 'fixed inset-0 z-50' : ''}`}>
      {/* Top Navigation */}
      <div className="bg-gray-800 border-b border-gray-700 px-4 py-2">
        <div className="flex items-center space-x-2 text-sm text-gray-400">
          <span>CATEGORY</span>
          <ChevronDown className="w-3 h-3 rotate-90" />
          <span>Minifiers</span>
          <ChevronDown className="w-3 h-3 rotate-90" />
          <span className="text-blue-400">JS Minifier</span>
        </div>
      </div>

      {/* Main Header */}
      <div className="bg-gray-900 border-b border-gray-700 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-3">
              <Code className="w-6 h-6 text-yellow-400" />
              <h1 className="text-xl font-semibold text-white">Advanced JavaScript Minifier</h1>
            </div>
            
            <div className="flex items-center bg-gray-800 rounded-lg border border-gray-700">
              <button
                onClick={() => setViewMode('code')}
                className={`px-4 py-2 text-sm font-medium rounded-l-lg transition-colors ${
                  viewMode === 'code' ? 'bg-gray-700 text-white' : 'text-gray-400 hover:text-white'
                }`}
              >
                <FileText className="w-4 h-4 inline mr-2" />
                Code
              </button>
              <button
                onClick={() => setViewMode('preview')}
                className={`px-4 py-2 text-sm font-medium transition-colors ${
                  viewMode === 'preview' ? 'bg-gray-700 text-white' : 'text-gray-400 hover:text-white'
                }`}
              >
                <Eye className="w-4 h-4 inline mr-2" />
                Preview
              </button>
              <button
                onClick={() => setViewMode('split')}
                className={`px-4 py-2 text-sm font-medium rounded-r-lg transition-colors ${
                  viewMode === 'split' ? 'bg-gray-700 text-white' : 'text-gray-400 hover:text-white'
                }`}
              >
                Split
              </button>
            </div>
          </div>

          <div className="flex items-center space-x-2">
            <button
              onClick={handleMinify}
              className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg flex items-center space-x-2 transition-colors font-medium"
            >
              <Play className="w-4 h-4" />
              <span>Run</span>
            </button>
            
            <button
              onClick={handleUpload}
              className="bg-blue-600 hover:bg-blue-700 text-white p-2 rounded-lg transition-colors"
              title="Upload JavaScript file"
            >
              <Upload className="w-4 h-4" />
            </button>
            
            <button
              onClick={handleDownload}
              disabled={!outputJS}
              className="bg-gray-700 hover:bg-gray-600 disabled:bg-gray-800 disabled:cursor-not-allowed text-white p-2 rounded-lg transition-colors"
              title="Download minified JavaScript"
            >
              <Download className="w-4 h-4" />
            </button>
            
            <button
              onClick={handleCopy}
              disabled={!outputJS}
              className="bg-gray-700 hover:bg-gray-600 disabled:bg-gray-800 disabled:cursor-not-allowed text-white p-2 rounded-lg transition-colors"
              title="Copy to clipboard"
            >
              <Copy className="w-4 h-4" />
            </button>
            
            <button
              onClick={handleSave}
              className="bg-gray-700 hover:bg-gray-600 text-white p-2 rounded-lg transition-colors"
              title="Save to local storage"
            >
              <Save className="w-4 h-4" />
            </button>
            
            <button
              onClick={handleReset}
              className="bg-gray-700 hover:bg-gray-600 text-white p-2 rounded-lg transition-colors"
              title="Reset all"
            >
              <RotateCcw className="w-4 h-4" />
            </button>
            
            <button
              className="bg-gray-700 hover:bg-gray-600 text-white p-2 rounded-lg transition-colors"
              title="Settings"
            >
              <Settings className="w-4 h-4" />
            </button>
            
            <button
              onClick={() => setIsFullscreen(!isFullscreen)}
              className="bg-gray-700 hover:bg-gray-600 text-white p-2 rounded-lg transition-colors"
              title="Toggle fullscreen"
            >
              {isFullscreen ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
            </button>
          </div>
        </div>
      </div>

      {/* Code Issues */}
      {codeIssues.length > 0 && (
        <div className="bg-gray-900 border-b border-gray-700 px-6 py-3">
          <div className="flex items-center space-x-2 text-yellow-400">
            <AlertCircle className="w-4 h-4" />
            <span className="text-sm font-medium">Code Issues ({codeIssues.length})</span>
          </div>
          <div className="mt-2 space-y-1">
            {codeIssues.slice(0, 3).map((issue, index) => (
              <div key={index} className="flex items-center space-x-2 text-sm">
                <span className={`text-xs px-2 py-1 rounded ${
                  issue.type === 'warning' ? 'bg-yellow-600' : 'bg-blue-600'
                }`}>
                  {issue.type}
                </span>
                <span className="text-gray-400">Line {issue.line}:</span>
                <span className="text-gray-300">{issue.message}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Editor Status Bar */}
      <div className="bg-gray-800 border-b border-gray-700 px-6 py-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-6 text-sm text-gray-400">
            <div className="flex items-center space-x-2">
              <ChevronDown className="w-4 h-4" />
              <span className="text-white">JavaScript Editor</span>
              <span className="text-green-400">✓ Auto-saved</span>
            </div>
            <span>{lineCount} lines</span>
            <span>{charCount} chars</span>
            <span>{wordCount} words</span>
            {compressionRatio > 0 && (
              <>
                <span>Original: {formatBytes(originalSize)}</span>
                <span>Minified: {formatBytes(minifiedSize)}</span>
                <span className="text-green-400">Saved: {compressionRatio}%</span>
              </>
            )}
          </div>
          <div className="flex items-center space-x-2 text-sm text-gray-400">
            <span>Live Preview</span>
            <div className="w-2 h-2 bg-green-400 rounded-full"></div>
            <span className="text-green-400">Live</span>
          </div>
        </div>
      </div>

      {/* Minification Options - Fixed Position */}
      <div className="bg-gray-800 border-b border-gray-700 px-6 py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <h3 className="text-sm font-medium text-white">Minification Options:</h3>
            <div className="flex flex-wrap gap-4">
              {Object.entries(options).map(([key, value]) => (
                <label key={key} className="flex items-center space-x-2 text-sm cursor-pointer">
                  <input
                    type="checkbox"
                    checked={value}
                    onChange={(e) => setOptions(prev => ({ ...prev, [key]: e.target.checked }))}
                    className="w-4 h-4 text-blue-600 bg-gray-700 border-gray-600 rounded focus:ring-blue-500 focus:ring-2"
                  />
                  <span className="text-gray-300">
                    {key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
                  </span>
                </label>
              ))}
            </div>
          </div>
          
          <div className="flex items-center space-x-4 text-sm text-gray-400">
            <div className="flex items-center space-x-2">
              <Shield className="w-4 h-4" />
              <span>Secure Processing</span>
            </div>
            <div className="flex items-center space-x-2">
              <Zap className="w-4 h-4" />
              <span>Real-time</span>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex" style={{ height: 'calc(100vh - 280px)' }}>
        {/* Left Pane - Input */}
        {(viewMode === 'code' || viewMode === 'split') && (
          <div className="flex-1 flex flex-col bg-gray-900 border-r border-gray-700">
            <div className="flex-1 relative">
              <div className="absolute inset-0 flex">
                {/* Line numbers */}
                <div className="w-12 bg-gray-800 border-r border-gray-700 text-right py-4 text-sm text-gray-500 font-mono leading-6 overflow-hidden">
                  {inputJS.split('\n').map((_, index) => (
                    <div key={index} className="px-2 h-6">
                      {index + 1}
                    </div>
                  ))}
                </div>
                
                {/* Code editor */}
                <div className="flex-1">
                  <textarea
                    value={inputJS}
                    onChange={(e) => setInputJS(e.target.value)}
                    className="w-full h-full bg-transparent text-white font-mono text-sm leading-6 p-4 resize-none focus:outline-none"
                    placeholder="Enter your JavaScript code here..."
                    spellCheck={false}
                    style={{ 
                      tabSize: 2,
                      fontFamily: 'JetBrains Mono, Consolas, Monaco, monospace'
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Right Pane - Output/Preview */}
        {(viewMode === 'preview' || viewMode === 'split') && (
          <div className="flex-1 flex flex-col bg-gray-900">
            <div className="flex-1 relative">
              <div className="absolute inset-0 flex">
                {/* Line numbers */}
                <div className="w-12 bg-gray-800 border-r border-gray-700 text-right py-4 text-sm text-gray-500 font-mono leading-6 overflow-hidden">
                  {outputJS.split('\n').map((_, index) => (
                    <div key={index} className="px-2 h-6">
                      {index + 1}
                    </div>
                  ))}
                </div>
                
                {/* Output display */}
                <div className="flex-1">
                  <pre className="w-full h-full bg-transparent text-gray-300 font-mono text-sm leading-6 p-4 overflow-auto whitespace-pre-wrap break-words">
                    {outputJS || '// Minified JavaScript will appear here after running the minifier...'}
                  </pre>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        accept=".js,application/javascript,text/javascript"
        onChange={handleFileChange}
        className="hidden"
      />
    </div>
  );
};

export default JSMinifier;